import {
  Button,
  HTMLSelect,
  Loading,
  Select,
  TimePicker,
  ToggleSwitch,
  WeekDaysPicker,
} from '@therms/atalaya'
import {
  Alerts,
  CHANNEL_POLICY_INHERIT_VALUE,
  type ChannelPolicyLeaf,
  type ChannelPolicyValue,
  daysToWeek,
  EMAIL_POLICY_OPTIONS,
  formatInheritOptionLabel,
  formatWindowDays,
  hhmmToISO,
  isChannelEnabled,
  isOvernightWindow,
  isoToHHmm,
  NOTIFICATION_CATEGORY_LABELS,
  OFF_DUTY_POLICY_OPTIONS,
  readChannelPolicyLeaf,
  removeChannelPolicyLeaf,
  setChannelPolicyLeaf,
  toggleCategoryChannel,
  useRPCContext,
  weekToDays,
} from '@therms/react-modules'
import {RPCDocs} from '@therms/rpc-gateway'
import {useEffect, useRef, useState} from 'react'

import Category = RPCDocs.Notifications.GetMyNotificationPreferences.v1.Category

type NotificationPreferences =
  RPCDocs.Notifications.GetMyNotificationPreferences.v1.NotificationPreferences
type SetArgs = RPCDocs.Notifications.SetMyNotificationPreferences.v1.Args
type QuietWindow = RPCDocs.Notifications.GetMyNotificationPreferences.v1.Window
type ChannelPolicies =
  RPCDocs.Notifications.GetMyNotificationPreferences.v1.ChannelPolicies

const CATEGORY_ORDER = [
  Category.ReportReview,
  Category.EventOpened,
  Category.EventAssignment,
  Category.System,
  Category.ShiftRequest,
]

const CHANNEL_POLICY_CONTROLS: Array<{
  label: string
  leaf: ChannelPolicyLeaf
  options: typeof EMAIL_POLICY_OPTIONS | typeof OFF_DUTY_POLICY_OPTIONS
  subText?: string
}> = [
  {label: 'Email', leaf: 'email', options: EMAIL_POLICY_OPTIONS},
  {
    label: 'Push — when off duty',
    leaf: 'push',
    options: OFF_DUTY_POLICY_OPTIONS,
    subText: 'On duty, push always reaches you.',
  },
  {
    label: 'SMS — when off duty',
    leaf: 'sms',
    options: OFF_DUTY_POLICY_OPTIONS,
    subText: 'On duty, SMS always reaches you.',
  },
]

// supportedValuesOf needs lib es2022.intl — cast if the app's tsconfig lib
// target predates it (runtime support is fine on Node 22 / evergreen browsers)
const TIMEZONE_OPTIONS = (
  Intl as unknown as {supportedValuesOf: (key: 'timeZone') => string[]}
)
  .supportedValuesOf('timeZone')
  .map((tz) => ({label: tz, value: tz}))

interface WindowDraft {
  days?: QuietWindow['days']
  end: string
  start: string
}

function NotificationSettings() {
  const {rpcService} = useRPCContext()

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)
  const [orgDefaults, setOrgDefaults] = useState<ChannelPolicies | undefined>(
    undefined,
  )
  const [windowDraft, setWindowDraft] = useState<WindowDraft | null>(null)
  const [editingWindowIndex, setEditingWindowIndex] = useState<number | null>(
    null,
  )

  useEffect(() => {
    rpcService.Notifications.GetMyNotificationPreferences.v1
      .call()
      .then(({data}) => {
        setPrefs(data.notificationPreferences)
        setOrgDefaults(data.organizationDefaults?.channelPolicies)
      })
      .catch(() =>
        Alerts.error('There was a problem loading notification preferences'),
      )
  }, [])

  /**
   * Partial upsert (contract): send ONLY the changed top-level field. The
   * response is the full saved record — it becomes the new state. On error,
   * state is untouched (controls render from state, so they revert).
   */
  const save = async (partial: SetArgs, opts?: {silent?: boolean}) => {
    try {
      const {data} =
        await rpcService.Notifications.SetMyNotificationPreferences.v1.call(
          partial,
        )

      setPrefs(data.notificationPreferences)
      if (!opts?.silent) Alerts.success('Preferences saved')
    } catch (err: any) {
      Alerts.error('There was a problem saving preferences', err?.message)
    }
  }

  // No timezone saved yet → adopt the browser's timezone and persist it right
  // away, silently (a "Preferences saved" toast on mount would be noise). The
  // ref guards StrictMode double-fire and re-fires from unrelated saves.
  const defaultedTimezone = useRef(false)
  useEffect(() => {
    if (!prefs || prefs.timezone || defaultedTimezone.current) return

    defaultedTimezone.current = true
    save(
      {timezone: Intl.DateTimeFormat().resolvedOptions().timeZone} as SetArgs,
      {silent: true},
    )
  }, [prefs])

  if (!prefs) {
    return (
      <div className="p-xl relative">
        <Loading overlay size="xxl" />
      </div>
    )
  }

  const quietHours = prefs.quietHours ?? {enabled: false, windows: []}
  const showAlwaysOnWarning =
    !quietHours.enabled || !quietHours.windows.length

  const saveWindows = (windows: QuietWindow[]) =>
    save({quietHours: {enabled: quietHours.enabled, windows}} as SetArgs)

  const commitWindowDraft = () => {
    if (!windowDraft) return

    const windows = [...quietHours.windows]

    if (editingWindowIndex === null) {
      windows.push(windowDraft as QuietWindow)
    } else {
      windows[editingWindowIndex] = windowDraft as QuietWindow
    }

    saveWindows(windows)
    setWindowDraft(null)
    setEditingWindowIndex(null)
  }

  return (
    <div className="space-y-xl pb-xl">
      {/* The delivery model in one sentence — contract copy, verbatim */}
      <div className="border-info bg-info-faded rounded-lg border p-sm text-sm">
        <span className="font-semibold">Inbox always.</span> On duty, push and
        SMS always reach you. Off duty, your quiet hours decide. Email always
        arrives unless you turn it down.
      </div>

      {/* 1 — Quiet hours + timezone */}
      <section className="space-y-sm">
        <div className="gap-sm flex items-center">
          <div className="text-color-strong grow font-semibold">
            Quiet hours
          </div>
          <ToggleSwitch
            active={quietHours.enabled}
            name="quiet_hours_enabled"
            onChangeValue={(enabled) =>
              save({
                quietHours: {enabled, windows: quietHours.windows},
              } as SetArgs)
            }
          />
        </div>
        <div className="text-color-subtle text-sm">
          Push, SMS, and (optionally) email wait until the window ends. Your
          inbox still updates instantly.
        </div>

        {showAlwaysOnWarning && (
          <div className="border-caution bg-caution-faded rounded-lg border p-sm text-sm">
            <span className="font-semibold">No quiet hours set?</span> Off-duty
            push and SMS deliver around the clock. Add windows to get silence
            when you&apos;re off.
          </div>
        )}

        <div className="space-y-xs">
          {quietHours.windows.map((window, index) => (
            <div
              className="border-border bg-surface gap-sm p-xs flex flex-wrap items-center rounded-lg border text-sm"
              key={`${window.start}-${window.end}-${index}`}
            >
              <span className="text-color-strong font-medium">
                {window.start} → {window.end}
              </span>
              {isOvernightWindow(window) && (
                <span className="text-color-subtle text-xs">overnight</span>
              )}
              <span className="text-color-subtle grow">
                {formatWindowDays(window.days)}
              </span>
              <Button
                onClick={() => {
                  setEditingWindowIndex(index)
                  setWindowDraft({...window})
                }}
                size="xs"
                subtle
              >
                Edit
              </Button>
              <Button
                onClick={() =>
                  saveWindows(
                    quietHours.windows.filter((_, i) => i !== index),
                  )
                }
                size="xs"
                subtle
                variant="critical"
              >
                Remove
              </Button>
            </div>
          ))}
        </div>

        {/* Explicit-confirm window editor — the one auto-save exception */}
        {windowDraft ? (
          <div className="border-border bg-surface space-y-sm rounded-lg border border-dashed p-sm">
            <div className="gap-sm flex flex-wrap items-center">
              <div>
                <div className="text-color-subtle text-xs">Start</div>
                <TimePicker
                  hideClearButton
                  onChangeValue={(iso) =>
                    iso &&
                    setWindowDraft({...windowDraft, start: isoToHHmm(iso)})
                  }
                  value={hhmmToISO(windowDraft.start)}
                />
              </div>
              <div>
                <div className="text-color-subtle text-xs">End</div>
                <TimePicker
                  hideClearButton
                  onChangeValue={(iso) =>
                    iso && setWindowDraft({...windowDraft, end: isoToHHmm(iso)})
                  }
                  value={hhmmToISO(windowDraft.end)}
                />
              </div>
            </div>

            {isOvernightWindow(windowDraft) && (
              <div className="text-color-subtle text-xs">
                Overnight window — runs past midnight into the next day.
              </div>
            )}

            <WeekDaysPicker
              label="Days"
              name="window_days"
              onChangeValue={(week) =>
                setWindowDraft({...windowDraft, days: weekToDays(week)})
              }
              value={daysToWeek(windowDraft.days)}
            />

            <div className="gap-xs flex justify-end">
              <Button
                onClick={() => {
                  setWindowDraft(null)
                  setEditingWindowIndex(null)
                }}
                size="sm"
                subtle
              >
                Cancel
              </Button>
              <Button onClick={commitWindowDraft} size="sm" variant="main">
                Save window
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => {
              setEditingWindowIndex(null)
              setWindowDraft({end: '07:00', start: '22:00'})
            }}
            size="sm"
            subtle
            variant="main"
          >
            + Add window
          </Button>
        )}

        <Select
          isSearchable
          label="Timezone"
          // No unset/clear path: the set RPC's timezone field is a plain
          // string that can't be unset over the wire (`undefined` is dropped
          // by JSON serialization, `null`/`''` fail schema validation), so
          // the browser's timezone is saved automatically on first load and
          // the field always holds a concrete zone from then on.
          onChangeValue={(value) => {
            if (value && value !== prefs.timezone) {
              save({timezone: value} as SetArgs)
            }
          }}
          options={TIMEZONE_OPTIONS}
          subText="Used to evaluate quiet hours. Defaults to your device's timezone."
          value={prefs.timezone ?? ''}
        />
      </section>

      {/* 2 — Global channel policies */}
      <section className="space-y-sm">
        <div>
          <div className="text-color-strong font-semibold">
            Delivery policies
          </div>
          <div className="text-color-subtle text-sm">
            How each channel behaves overall. Push and SMS settings apply only
            while you&apos;re off duty.
          </div>
        </div>

        {/*
          Inherit-as-option: the first option IS the inherited state (labeled with
          provenance). A bare value in the select always means "explicitly pinned".
          Contract: this screen never builds channelPolicies objects — the utils are
          the only place raw policy objects are constructed (whole-object replace;
          reset = leaf removal, never a value write).
        */}
        {CHANNEL_POLICY_CONTROLS.map(({label, leaf, options, subText}) => (
          <HTMLSelect
            key={leaf}
            label={label}
            onChangeValue={(value) =>
              save({
                channelPolicies:
                  value === CHANNEL_POLICY_INHERIT_VALUE
                    ? removeChannelPolicyLeaf(prefs.channelPolicies, leaf)
                    : setChannelPolicyLeaf(
                        prefs.channelPolicies,
                        leaf,
                        value as ChannelPolicyValue,
                      ),
              } as SetArgs)
            }
            options={[
              {
                label: formatInheritOptionLabel(leaf, orgDefaults),
                value: CHANNEL_POLICY_INHERIT_VALUE,
              },
              ...options,
            ]}
            subText={subText}
            value={
              readChannelPolicyLeaf(prefs.channelPolicies, leaf) ??
              CHANNEL_POLICY_INHERIT_VALUE
            }
          />
        ))}
      </section>

      {/* 3 — Per-category channel toggles. NO in-app toggle, ever. */}
      <section className="space-y-sm">
        <div>
          <div className="text-color-strong font-semibold">
            Notification types
          </div>
          <div className="text-color-subtle text-sm">
            Choose channels per notification type. In-app can&apos;t be turned
            off — your inbox is the record.
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-color-subtle border-border border-b text-left">
                <th className="p-xs font-medium">Type</th>
                <th className="p-xs text-center font-medium">In-app</th>
                <th className="p-xs text-center font-medium">Push</th>
                <th className="p-xs text-center font-medium">Email</th>
                <th className="p-xs text-center font-medium">SMS</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORY_ORDER.map((category) => (
                <tr className="border-border border-b" key={category}>
                  <td className="p-xs">
                    {NOTIFICATION_CATEGORY_LABELS[category]}
                  </td>
                  <td className="text-color-subtle p-xs text-center">
                    Always ✓
                  </td>
                  {(['push', 'email', 'sms'] as const).map((channel) => (
                    <td className="p-xs text-center" key={channel}>
                      <ToggleSwitch
                        active={isChannelEnabled(
                          prefs.categoryChannelPrefs,
                          category,
                          channel,
                        )}
                        name={`${category}_${channel}`}
                        onChangeValue={(active) =>
                          save({
                            categoryChannelPrefs: toggleCategoryChannel(
                              prefs.categoryChannelPrefs,
                              category,
                              channel,
                              active,
                            ),
                            // Get/Set namespaces duplicate identical enums —
                            // values are the same strings
                          } as unknown as SetArgs)
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  )
}

export {NotificationSettings}
